import { computeHorizontalOverflow, subscribeHorizontalOverflow } from '@/utils/horizontalOverflow.js'

/**
 * Builds a Vue 2 mixin that keeps `$_horizontalOverflow_isOverflowing` in sync with horizontal
 * scroll overflow on the element at `this.$refs[scrollRef]`, using {@link subscribeHorizontalOverflow}.
 *
 * The flag lives on `data` as `$_horizontalOverflow_isOverflowing`. It is not proxied on the Vue 2
 * instance (names starting with `$` / `_`). In script use
 * `this.$data.$_horizontalOverflow_isOverflowing`; in templates use
 * `$data.$_horizontalOverflow_isOverflowing`.
 *
 * Exposes `$_horizontalOverflow_sync`, `$_horizontalOverflow_setup`, and
 * `$_horizontalOverflow_teardown` for components that must re-measure after async layout
 * (call `$_horizontalOverflow_sync` in `$nextTick` after content changes).
 *
 * Do not define your own `data` entry for `$_horizontalOverflow_isOverflowing`; reserve it for
 * this mixin (or use the utils directly instead of the mixin).
 *
 * @param {object} options
 * @param {string} options.scrollRef Ref name of the scroll container element.
 * @returns {object} Vue component options (mixin object).
 */
export function createHorizontalOverflowMixin ({ scrollRef }) {
  if (!scrollRef) {
    throw new Error('createHorizontalOverflowMixin: `scrollRef` is required')
  }

  return {
    data () {
      return {
        $_horizontalOverflow_isOverflowing: false
      }
    },
    created () {
      /** @type {(() => void)|null} */
      this.$_horizontalOverflowDispose = null
      this.$_horizontalOverflowScrollRef = scrollRef
    },
    mounted () {
      this.$nextTick(() => {
        this.$_horizontalOverflow_setup()
        this.$_horizontalOverflow_sync()
      })
    },
    beforeDestroy () {
      this.$_horizontalOverflow_teardown()
    },
    methods: {
      $_horizontalOverflow_sync () {
        const el = this.$refs[this.$_horizontalOverflowScrollRef]
        this.$data.$_horizontalOverflow_isOverflowing = computeHorizontalOverflow(el)
      },
      $_horizontalOverflow_setup () {
        this.$_horizontalOverflow_teardown()
        const el = this.$refs[this.$_horizontalOverflowScrollRef]
        if (!el) return
        this.$_horizontalOverflowDispose = subscribeHorizontalOverflow(el, () => {
          this.$_horizontalOverflow_sync()
        })
      },
      $_horizontalOverflow_teardown () {
        if (this.$_horizontalOverflowDispose) {
          this.$_horizontalOverflowDispose()
          this.$_horizontalOverflowDispose = null
        }
      }
    }
  }
}
